import { useState, useEffect, lazy, Suspense } from 'react';
import { useStore } from '@/store/useStore';
import { socket, connectUser } from '@/utils/socket';
import { apiFetch } from '@/utils/api';
import { useTranslation } from 'react-i18next';
import BrandItemDetails from './pages/BrandItemDetails';
import BrandAuctionDetails from './pages/BrandAuctionDetails';
import Usernames from './pages/Usernames';
import AdminDashboard from './pages/AdminDashboard';
import { TabNavigator } from '@/navigation/TabNavigator';
import { AnimatePresence, motion } from 'framer-motion';
import { ScrollButtons } from '@/components/ui/ScrollButtons';
import { ToastNotification } from '@/components/Feedback/ToastNotification';
import { NotificationProvider } from '@/context/NotificationContext';
import { AppProvider } from '@/context/AppContext';
import { invalidateAllCache } from '@/utils/cache';

// Lazy load pages
import Home from '@/pages/Home';
import Store from '@/pages/Store';
import MyUser from '@/pages/MyUser';
import Offers from '@/pages/Offers';
const OffersActivity = lazy(() => import('@/pages/OffersActivity'));
import OfferDetailsPage from '@/pages/OfferDetailsPage';
import Auctions from '@/pages/Market/AuctionHouse';
const Staking = lazy(() => import('@/pages/Staking'));
import { useTelegramBackButton } from '@/hooks/useTelegram';

const AddUsername = lazy(() => import('@/pages/AddUsername'));
import ProfilePush from '@/pages/ProfilePush';
import ProfileHome from '@/pages/Profile/ProfileHome';
const Wallet = lazy(() => import('@/pages/Wallet'));
const PremiumItemPage = lazy(() => import('@/pages/PremiumItemPage'));
const AllItems = lazy(() => import('@/pages/AllItems'));
const CardDetailsPage = lazy(() => import('@/pages/CardDetailsPage'));
const P2PCompletedDetail = lazy(() => import('@/pages/P2PCompletedDetail'));
const P2PTransactionPage = lazy(() => import('@/pages/P2PTransactionPage'));
const AuctionBid = lazy(() => import('@/pages/AuctionBid'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const UsernameMarket = lazy(() => import('@/pages/UsernameMarket'));
const MyUsernameActivity = lazy(() => import('@/pages/MyUsernameActivity'));
const ContractsPage = lazy(() => import('@/pages/ContractsPage'));
const MyNFTs = lazy(() => import('@/pages/Profile/MyInventory'));
const NFTDetailsPage = lazy(() => import('@/pages/NFTDetailsPage'));
const UnifiedReceiptPage = lazy(() => import('@/pages/UnifiedReceiptPage'));
const ContractVerificationPage = lazy(() => import('@/pages/ContractVerificationPage'));
const LevelsPage = lazy(() => import('@/pages/LevelsPage'));
const SkinsPage = lazy(() => import('@/pages/SkinsPage'));
const WalletSkinsStore = lazy(() => import('@/pages/WalletSkinsStore'));
const PacksStore = lazy(() => import('@/pages/PacksStore'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const Settings = lazy(() => import('@/pages/Settings'));
const BillingHistory = lazy(() => import('@/pages/Profile/BillingHistory'));
const PurchasesPage = lazy(() => import('@/pages/PurchasesPage'));
const P2PDealPage = lazy(() => import('@/pages/P2PDealPage'));
const AppOrderPage = lazy(() => import('@/pages/AppOrderPage'));
const OrderTreeLineagePage = lazy(() => import('@/pages/OrderTreeLineagePage'));

const UserCollection = lazy(() => import('@/pages/UserCollection'));
const OwnedItemDetails = lazy(() => import('@/pages/OwnedItemDetails'));
const UserSkins = lazy(() => import('@/pages/UserSkins'));
const Chat = lazy(() => import('@/pages/Chat'));
const UserSearch = lazy(() => import('@/pages/UserSearch'));
const PublicProfile = lazy(() => import('@/pages/PublicProfile'));
const UserUsernames = lazy(() => import('@/pages/UserUsernames'));
const PrivateChat = lazy(() => import('@/pages/PrivateChat'));
const ChatInbox = lazy(() => import('@/pages/Profile/MyChats'));
import Leaderboard from '@/pages/Leaderboard';
const CardPreviewPage = lazy(() => import('@/pages/CardPreviewPage'));
const AdminSpecialCardPage = lazy(() => import('@/pages/AdminSpecialCardPage'));
const AdminEmojiManager = lazy(() => import('@/pages/AdminEmojiManager').then(m => ({ default: m.AdminEmojiManager })));
const GiftsPage = lazy(() => import('@/pages/GiftsPage').then(m => ({ default: m.GiftsPage })));


import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { PyramidLoader } from '@/components/ui/PyramidLoader';
import TemplatePreview from '@/pages/TemplatePreview';
import GiftRevealModal from '@/components/Modals/GiftRevealModal';
import ChannelSubscribeModal from '@/components/Modals/ChannelSubscribeModal';
import LanguageSelectModal from '@/components/Modals/LanguageSelectModal';

function App() {
    const { t: tErrors } = useTranslation('errors');
    const { t: tNotif } = useTranslation('notifications');
    const { user, fetchUser, setWallet, hydrateFromCache, pageLoading, setPageLoading, fetchTileBgs, fetchAnimationConfig, setCurrentRoute } = useStore();
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const [activePremiumItem, setActivePremiumItem] = useState<any>(null);
    const [currentNotification, setCurrentNotification] = useState<any>(null);
    const [activeNFT, setActiveNFT] = useState<any>(null);
    const [activeReceiptData, setActiveReceiptData] = useState<any>(null);
    const [appReady, setAppReady] = useState(false);
    const [newGifts, setNewGifts] = useState<any[]>([]);

    // ─── Hydrate from localStorage IMMEDIATELY (zero-wait startup) ───
    // This runs synchronously before any API call so UI shows cached data instantly
    useState(() => { hydrateFromCache(); });

    // Environment Check
    const [isTelegram, setIsTelegram] = useState(true);

    useEffect(() => {
        // Purge old stale local storage cache keys once
        try { invalidateAllCache(); } catch (e) {}

        // Basic check: is platform available?
        const tg: any = window.Telegram?.WebApp;
        const urlParams = new URLSearchParams(window.location.search);
        const isRecording = urlParams.get('record') === 'true';
        const isPreview = urlParams.get('preview') === 'true';

        // Strict Protection: Block access if NOT in Telegram AND NOT in specialized modes
        if (!isRecording && !isPreview && (!tg || !tg.initData)) {
            console.warn("Access Denied: Not in Telegram");
            setIsTelegram(false);
            return () => {};
        }

        if (tg) {
            // Early Fullscreen Initialization
            if (typeof tg.ready === 'function') tg.ready();
            if (typeof tg.expand === 'function') tg.expand();
            if (typeof tg.requestFullscreen === 'function') {
                try { tg.requestFullscreen(); } catch (e) {}
            }

            // Set Header/Back Colors
            if (typeof tg.setHeaderColor === 'function') tg.setHeaderColor('#020408');
            if (typeof tg.setBackgroundColor === 'function') tg.setBackgroundColor('#020408');

            // iOS-style nav hide on scroll — direct DOM class toggle (NO React re-renders)
            let lastScrollY = 0;
            let navScrollRAF = 0;
            const rootElem = document.getElementById('root');

            const handleScroll = () => {
                const root = rootElem;
                if (!root) return;
                if (navScrollRAF) return;
                navScrollRAF = requestAnimationFrame(() => {
                    navScrollRAF = 0;
                    const y = root.scrollTop;
                    const dy = y - lastScrollY;
                    const nav = document.getElementById('tabnav');
                    if (!nav) return;
                    if (dy > 4 && y > 40) nav.classList.add('nav-slide-down');
                    else if (dy < -4) nav.classList.remove('nav-slide-down');
                    lastScrollY = y;
                });
            };

            if (rootElem) {
                rootElem.addEventListener('scroll', handleScroll);
            }

            // Safe Area & UI Updates (Use stable viewport height like shorts app)
            const getStableH = () => tg.viewportStableHeight || tg.viewportHeight || window.innerHeight;
            document.documentElement.style.setProperty('--tg-viewport-height', `${getStableH()}px`);
            document.documentElement.style.setProperty('--tg-safe-area-inset-top', `${tg.contentSafeAreaInset?.top || 0}px`);
            document.documentElement.style.setProperty('--tg-safe-area-inset-bottom', `${tg.contentSafeAreaInset?.bottom || 0}px`);

            // Events
            tg.onEvent?.('viewportChanged', () => {
                document.documentElement.style.setProperty('--tg-viewport-height', `${getStableH()}px`);
            });

            const loadStartTime = Date.now();
            const minDisplayTime = 700;

            // Maximum limit: Guarantee app opens in 4 seconds max, no matter network speed
            let isDone = false;
            const finishLoading = () => {
                if (!isDone) {
                    isDone = true;
                    setAppReady(true);
                }
            };

            const forceTimeout = setTimeout(() => {
                console.warn("[App] Loading reached 4s maximum threshold. Opening app now.");
                finishLoading();
            }, 4000);

            const syncAndFetch = async () => {
                const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
                
                // PERFORMANCE FIX: If in preview/recording mode, bypass sync & min loading time
                if (isPreview || isRecording) {
                    console.log("[App] Preview/Record mode detected, skipping sync.");
                    clearTimeout(forceTimeout);
                    finishLoading();
                    return;
                }

                if (tgUser) {
                    try {
                        connectUser(tgUser.id);

                        // Timeout safety wrapper for network promises (max 3.5s timeout per request)
                        const timeoutPromise = new Promise(res => setTimeout(() => res(null), 3500));

                        const fetchSync = apiFetch('/api/users/sync', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(tgUser)
                        }).catch(e => {
                            console.error('Sync request error', e);
                            return null;
                        });

                        const [syncRes] = await Promise.race([
                            Promise.all([
                                fetchSync,
                                fetchUser(tgUser.id, true).catch(e => { console.error('fetchUser failed', e); return null; }),
                                fetchTileBgs().catch(e => { console.error('fetchTileBgs failed', e); return null; }),
                                fetchAnimationConfig().catch(e => { console.error('fetchAnimationConfig failed', e); return null; })
                            ]),
                            timeoutPromise.then(() => [null])
                        ]);

                        if (syncRes && typeof syncRes.json === 'function') {
                            const data = await syncRes.json().catch(() => ({}));
                            // Check if it's a new user with gifts
                            if (data?.is_new && data.initialGifts?.length > 0) {
                                setNewGifts(data.initialGifts);
                                // Preload images to prevent flickering in modal
                                data.initialGifts.forEach((c: any) => {
                                    const img = new Image();
                                    img.src = c.image_filename;
                                });
                            }
                        }

                        console.log('User Synced:', tgUser.id);
                    } catch (e) {
                        console.error('Sync failed', e);
                    }
                }

                // Unlock app as soon as the critical path is done (short branding flash, max 4s total)
                clearTimeout(forceTimeout);
                const timeSpent = Date.now() - loadStartTime;
                const remaining = Math.max(0, Math.min(minDisplayTime - timeSpent, 4000 - timeSpent));
                setTimeout(finishLoading, remaining > 0 ? remaining : 0);
            };
            syncAndFetch();

            return () => {
                if (navScrollRAF) cancelAnimationFrame(navScrollRAF);
                if (rootElem) {
                    rootElem.removeEventListener('scroll', handleScroll);
                }
            };
        }
    }, []);

    // Global Socket Listeners
    useEffect(() => {
        if (!user?.telegram_id) return;

        const userIdForSocket = user.telegram_id;

        const onConnect = () => {
            console.log("[Socket] Connected!", socket.id);
            connectUser(userIdForSocket);
        };

        const handleNotif = (notif: any) => {
            setUnreadNotifications(prev => prev + 1);
            const newNotif = { ...notif, id: notif.id || Date.now() };
            setCurrentNotification(newNotif);

            // Haptic
            const haptic = window.Telegram?.WebApp?.HapticFeedback;
            if (haptic) {
                haptic.notificationOccurred('success');
            }
        };

        if (socket.connected) onConnect();

        socket.on('connect', onConnect);
        socket.on('notification', handleNotif);
        socket.on('new_notification', handleNotif);

        // Private Chat Listener
        socket.on('new_private_message', (msg: any) => {
            console.log("[Socket] New Private Message:", msg);
            // Only show notification if not currently in that chat (simplification: show always for now)
            const notif = {
                id: Date.now(),
                title: tNotif('newMessage'),
                message: msg.content,
                type: 'private_chat',
                metadata: { sender_id: msg.sender_id }
            };
            handleNotif(notif);
        });

        socket.on('balance_update', (data: any) => {
            setWallet({ available_balance: data.available_balance });
        });

        return () => {
            socket.off('connect', onConnect);
            socket.off('notification', handleNotif);
            socket.off('new_notification', handleNotif);
            socket.off('new_private_message');
            socket.off('balance_update');
        };
    }, [user, setWallet]);

    const [activeTx, setActiveTx] = useState<any>(null);

    // P2P Deal: real-time via Socket.IO + 30s fallback poll (was 5s polling — 6x more efficient)
    useEffect(() => {
        const checkP2P = async () => {
            const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
            if (!tgUser) return;
            try {
                const res = await apiFetch(`/api/p2p/active?userId=${tgUser.id}`);
                const data = await res.json();
                if (data.success && data.transaction) {
                    setActiveTx(data.transaction);
                } else {
                    setActiveTx(null);
                }
            } catch (e) { console.error(e); }
        };

        checkP2P(); // Initial fetch
        const interval = setInterval(checkP2P, 30000); // Fallback poll every 30s (was 5s)

        // Real-time listener: instant update when deal status changes
        const handleP2PUpdate = (data: any) => {
            console.log('[Socket] p2p_update received:', data);
            if (data.status === 'cancelled' || data.status === 'completed' || data.status === 'archived') {
                setActiveTx(null); // Clear widget immediately
            } else {
                checkP2P(); // Refetch for new status
            }
        };

        socket.on('p2p_update', handleP2PUpdate);

        return () => {
            clearInterval(interval);
            socket.off('p2p_update', handleP2PUpdate);
        };
    }, []);

    useEffect(() => {
        try {
            const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
            if (startParam === 'about') {
                setActiveTab('about');
            } else if (startParam === 'terms') {
                setActiveTab('terms');
            } else if (startParam === 'store' || startParam === 'my_user' || startParam === 'offers' ||
                startParam === 'auctions' || startParam === 'purchases' || startParam === 'all_items' ||
                startParam === 'username_market' || startParam === 'staking' || startParam === 'profile') {
                setActiveTab(startParam);
                setHistory([startParam]);
            }
        } catch (e) {
            console.error("Failed to parse start_param on mount", e);
        }
    }, []);

    const [activeTab, setActiveTab] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('preview') === 'true' && urlParams.get('cardId')) {
            return 'preview';
        }
        if (urlParams.get('record') === 'true' && urlParams.get('cardId')) {
            return 'preview';
        }

        const path = window.location.pathname;
        if (path === '/admin') return 'admin';
        const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param || urlParams.get('startapp');
        if (startParam === 'about') return 'about';
        if (startParam === 'terms') return 'terms';
        if (startParam === 'staking' || startParam === 'store' || startParam === 'my_user' ||
            startParam === 'offers' || startParam === 'auctions' || startParam === 'purchases' ||
            startParam === 'all_items' || startParam === 'profile' || startParam === 'username_market') {
            return startParam;
        }
        if (startParam === 'admin_emojis') return 'admin_emojis';
        if (startParam && startParam.startsWith('card_')) return 'card_view';
        if (startParam && startParam.startsWith('list_')) return 'card_view'; // Will use same viewer but fetch listing data
        if (startParam && startParam.startsWith('auct_')) {
            const tempAuctId = startParam.replace('auct_', '');
            localStorage.setItem('selected_auction_id', tempAuctId);
            return 'auction_bid'; // Route to auction view
        }
        if (startParam && startParam.startsWith('spkg_')) return 'skins';
        if (startParam && startParam.startsWith('brand_')) return 'brand-item';
        if (startParam && (startParam.startsWith('pkg_') || startParam.startsWith('package_'))) {
            // Legacy/Intelligent routing: pkg_6 is a known skin package
            if (startParam === 'pkg_6') return 'skins';
            return 'all_items';
        }
        return 'store';
    });

    const [activeCardId, setActiveCardId] = useState<string | null>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('preview') === 'true') return urlParams.get('cardId');
        if (urlParams.get('record') === 'true') return urlParams.get('cardId');

        try {
            const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
            if (startParam && startParam.startsWith('card_')) {
                return startParam.replace('card_', '');
            }
            if (startParam && startParam.startsWith('list_')) {
                return startParam.replace('list_', ''); // We use activeCardId to pass the ID, even if it's a listing ID.
            }
            if (startParam && startParam.startsWith('auct_')) {
                // Return null here, let activeBrandAuctionId handle it, or we could use activeCardId as a generic ID passer.
                // We'll use activeBrandAuctionId for auctions below.
                return null;
            }
        } catch (e) {
            console.error("Error parsing start_param", e);
        }
        return null;
    });


    const [history, setHistory] = useState<string[]>(['store']);
    const [activeCategory, setActiveCategory] = useState<any>(null);
    const [ownedDraft, setOwnedDraft] = useState<any>(undefined);
    const [activePackageId, setActivePackageId] = useState<number | string | null>(() => {
        try {
            const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
            if (startParam) {
                if (startParam.startsWith('spkg_')) return startParam.replace('spkg_', '');
                if (startParam.startsWith('pkg_')) return startParam.replace('pkg_', '');
                if (startParam.startsWith('package_')) return startParam.replace('package_', '');
            }
        } catch (e) {
            console.error("Error parsing pkg start_param", e);
        }
        return null;
    });

    const mainTabs = ['home', 'store', 'my_user', 'offers', 'auctions', 'staking', 'profile'];

    const [manualP2PData, setManualP2PData] = useState<any>(null);

    const [activeBrandItemId, setActiveBrandItemId] = useState<any>(() => {
        try {
            const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
            if (startParam && startParam.startsWith('brand_')) {
                return startParam.replace('brand_', '');
            }
        } catch (e) { }
        return null;
    });
    const [activeBrandAuctionId, setActiveBrandAuctionId] = useState<string | number | null>(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('preview') === 'true' && urlParams.get('type') === 'auction') return urlParams.get('cardId');
        if (urlParams.get('record') === 'true' && urlParams.get('type') === 'auction') return urlParams.get('cardId');

        try {
            const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
            if (startParam && startParam.startsWith('auct_')) {
                return startParam.replace('auct_', '');
            }
        } catch (e) { }
        return null;
    });
    const [activeOfferItem, setActiveOfferItem] = useState<any>(null);


    const handleNavigate = (page: string, category?: any, opts?: { refresh?: boolean }) => {
        // Same-page navigation is normally a no-op, but a refresh flag (used
        // after a purchase) must re-enter the page so it reloads the item.
        if (page === activeTab && !opts?.refresh) return;

        if (mainTabs.includes(page)) {
            setActivePackageId(null);
            setActiveCategory(null);
        }

        setActiveTab(page);
        setCurrentRoute(page);

        // History Logic
        if (mainTabs.includes(page)) {
            setHistory([page]);
        } else {
            setHistory(prev => [...prev, page]);
        }

        if (page === 'brand-item' && category) setActiveBrandItemId(category);
        if (page === 'brand-auction' && category) setActiveBrandAuctionId(category);
        if (page === 'offer_view' && category) setActiveOfferItem(category);

        if (page === 'card_view' || page === 'owned_view') {
            if (typeof category === 'string' || typeof category === 'number') {
                setActiveCardId(String(category));
            } else if (category && typeof category === 'object') {
                setActiveCardId(category);
            }
        }

        if (page === 'auction_bid' && category) {
            if (typeof category === 'string' || typeof category === 'number') {
                localStorage.setItem('selected_auction_id', String(category));
            } else if (typeof category === 'object') {
                const targetAuctId = category.auctionId || category.id || category.auction_id || (category.umktId ? `umkt_${category.umktId}` : null);
                if (targetAuctId) localStorage.setItem('selected_auction_id', String(targetAuctId));
            }
        }

        if (page === 'owned_view' && category && typeof category === 'object') setOwnedDraft(category);
        if (page === 'premium_view' && typeof category === 'object') setActivePremiumItem(category);
        if ((page === 'p2p_transaction' || page === 'p2p_completed_detail') && typeof category === 'object') setManualP2PData(category);
        if (page === 'nft_details' && typeof category === 'object') setActiveNFT(category);
         if ((page === 'nft_receipt' || page === 'contract_receipt') && typeof category === 'object') setActiveReceiptData(category);
         if (page === 'p2p_deal') setActiveCategory(typeof category === 'number' ? category : (typeof category === 'string' ? Number(category) : null));
         if (page === 'order_tree' || page === 'order_lineage') setActiveCategory(category);

        if (page === 'all_items') {
            if (typeof category === 'object' && category !== null) {
                setActivePackageId(category.packageId || null);
                setActiveCategory(category.category || null);
            } else if (typeof category === 'string') {
                setActivePackageId(null);
                setActiveCategory(category);
            } else {
                setActivePackageId(null);
                setActiveCategory(null);
            }
        } else if (category && typeof category === 'string' && page !== 'card_view' && page !== 'owned_view' && page !== 'brand-item' && page !== 'brand-auction') {
            setActiveCategory(category);
        }

        if (page === 'public_profile' && typeof category === 'object') setActiveCategory(category);
        if (page === 'user_usernames') setActiveCategory(category ?? null);
        if (page === 'user_collection' || page === 'user_skins' || page === 'private_chat') setActiveCategory(category || null);
        if (page === 'all_items' && typeof category === 'object' && category.packageId) {
            setActiveCategory(category.category || 'All Items');
            setActivePackageId(category.packageId);
        }

        // ─── Only show a brief loading flash for SUB-PAGES, NOT main tabs ───
        // Main tabs show content instantly from memory/cache
        if (!mainTabs.includes(page)) {
            setPageLoading(true);
            setTimeout(() => setPageLoading(false), 150);
        }
    };

    const handleBack = () => {
        if (history.length > 1) {
            const newHistory = history.slice(0, -1);
            setHistory(newHistory);
            const newTab = newHistory[newHistory.length - 1];
            setActiveTab(newTab);
            setCurrentRoute(newTab);
        } else {
            // Fallback if history empty
            setActiveTab('store');
            setCurrentRoute('store');
            setHistory(['store']);
        }
    };

    // ─── Native Telegram SDK BackButton Binding ───
    const showTelegramBackButton = activeTab !== 'store' || history.length > 1;
    useTelegramBackButton(showTelegramBackButton, handleBack);

    // ─── Determine if we're on a sub-page (not a main tab) ───
    const isOnSubPage = !mainTabs.includes(activeTab) && activeTab !== 'admin' && activeTab !== 'preview';

    useEffect(() => {
        const tg = window.Telegram?.WebApp;
        if (tg && typeof tg.expand === 'function') {
            tg.expand();
        }
    }, [activeTab, isOnSubPage]);

    // ─── Render sub-page content (only when on a sub-page) ───
    const renderSubPage = () => {
        switch (activeTab) {
            case 'brand-item':
                return <BrandItemDetails id={activeBrandItemId!} onBack={handleBack} onNavigate={handleNavigate} />;
            case 'brand-auction':
                return <BrandAuctionDetails id={activeBrandAuctionId!} onBack={handleBack} />;
            case 'premium_view':
                return <PremiumItemPage item={activePremiumItem} onBack={handleBack} />;
            case 'wallet':
                return <Wallet onBack={handleBack} onNavigate={handleNavigate} initialDeposit={activeCategory === 'deposit'} />;
            case 'my_nfts':
                return <MyNFTs onBack={() => setActiveTab('wallet')} onNavigate={handleNavigate} />;
            case 'nft_details':
                return <NFTDetailsPage nft={activeNFT} onBack={handleBack} />;
            case 'nft_receipt':
                return <UnifiedReceiptPage data={activeReceiptData || {}} onBack={handleBack} />;
            case 'contract_receipt':
                return <ContractVerificationPage data={activeReceiptData || {}} onBack={handleBack} />;
            case 'add_username':
                return <AddUsername onBack={handleBack} />;
            case 'usernames':
                return <Usernames onBack={handleBack} onNavigate={handleNavigate} />;
            case 'all_items': {
                const sourceTab = history.length > 1 ? history[history.length - 2] : 'home';
                return <AllItems
                    key={activePackageId ? `pack-${activePackageId}` : 'all-items'}
                    onBack={handleBack}
                    category={activeCategory}
                    packageId={activePackageId || undefined}
                    source={sourceTab}
                    onNavigate={handleNavigate}
                />;
            }
            case 'card_view':
                return <CardDetailsPage cardId={activeCardId!} onBack={handleBack} onNavigate={handleNavigate} />;
            case 'p2p_transaction':
                if (!manualP2PData && !activeTx) return null;
                return <P2PTransactionPage transaction={manualP2PData || activeTx} onBack={handleBack} />;
            case 'p2p_completed_detail':
                if (!manualP2PData && !activeTx) return null;
                return <P2PCompletedDetail transaction={manualP2PData || activeTx} onBack={handleBack} />;
            case 'items_store':
                return <Store onNavigate={handleNavigate} isActive={false} />;
            case 'username_market':
                return <Store onNavigate={handleNavigate} isActive={true} />;
            case 'username_activity':
                return <MyUser onNavigate={handleNavigate} isActive={true} />;
            case 'offers_activity':
                return <OffersActivity onBack={handleBack} onNavigate={handleNavigate} />;
            case 'auction_bid':
                return <AuctionBid onBack={() => setActiveTab('auctions')} onNavigate={handleNavigate} />;
            case 'offer_view':
                return activeOfferItem
                    ? <OfferDetailsPage item={activeOfferItem} onBack={handleBack} onNavigate={handleNavigate} />
                    : null;
            case 'notifications':
                return <NotificationsPage onBack={() => setActiveTab('home')} onNavigate={handleNavigate} />;
            case 'p2p_history':
                return <ContractsPage
                    onBack={() => setActiveTab('home')}
                    onNavigateDetail={(dealId: string | number, status?: string) => {
                        const s = String(status).toLowerCase();
                        if (['completed', 'archived', 'cancelled', 'dispute_closed'].includes(s)) handleNavigate('p2p_completed_detail', { deal_id: dealId });
                        else handleNavigate('p2p_transaction', { deal_id: dealId });
                    }}
                    onNavigateReceipt={(data: any) => handleNavigate('contract_receipt', data)}
                    onNavigate={handleNavigate}
                />;
            case 'levels':
                return <LevelsPage onBack={handleBack} />;
            case 'skins':
                return <SkinsPage onBack={handleBack} initialPackageId={activePackageId} />;
            case 'wallet_skins':
                return <WalletSkinsStore onBack={handleBack} />;
            case 'user_collection':
                return <UserCollection userData={activeCategory} onBack={handleBack} onNavigate={handleNavigate} />;
            case 'user_skins':
                return <UserSkins userData={activeCategory} onBack={handleBack} onNavigate={handleNavigate} />;
            case 'chat':
                return <Chat onBack={handleBack} />;
            case 'search':
                return <UserSearch onBack={handleBack} onNavigate={handleNavigate} />;
            case 'public_profile':
                return <PublicProfile userData={activeCategory} onBack={handleBack} onNavigate={handleNavigate} />;
            case 'user_usernames':
                return <UserUsernames target={activeCategory} onBack={handleBack} onNavigate={handleNavigate} />;
            case 'private_chat':
                return <PrivateChat targetUser={activeCategory} onBack={handleBack} onNavigate={handleNavigate} />;
            case 'inbox':
                return <ChatInbox onBack={handleBack} onNavigate={handleNavigate} />;
            case 'packs_store':
                return <PacksStore onBack={handleBack} onNavigate={handleNavigate} />;
            case 'leaderboard':
                return <Leaderboard onBack={handleBack} onNavigate={handleNavigate} />;
            case 'terms':
                return <TermsPage onBack={handleBack} />;
            case 'about':
                return <AboutPage onBack={handleBack} />;
            case 'settings':
                return <Settings onBack={handleBack} />;
            case 'billing_history':
                return <BillingHistory onBack={handleBack} />;
            case 'purchases':
                 return <PurchasesPage onBack={handleBack} onNavigate={handleNavigate} />;
             case 'p2p_deal':
                 return <P2PDealPage dealId={Number(activeCategory)} onBack={handleBack} onNavigate={handleNavigate} />;
             case 'app_order':
                 return <AppOrderPage appOrderId={Number(activeCategory)} onBack={handleBack} onNavigate={handleNavigate} />;
             case 'order_tree':
             case 'order_lineage':
                 return <OrderTreeLineagePage orderData={activeCategory} onBack={handleBack} onNavigate={handleNavigate} />;
             case 'owned_view':
                return <OwnedItemDetails cardId={activeCardId!} ownedDraft={ownedDraft} onBack={handleBack} />;
            case 'admin_card':
                return <AdminSpecialCardPage onBack={handleBack} />;
            case 'admin_emojis':
                return <AdminEmojiManager onBack={handleBack} />;
            case 'template_preview':
                return <TemplatePreview onBack={handleBack} />;
            case 'gifts':
                return <GiftsPage />;
            case 'full_profile':
                return <ProfileHome onBack={handleBack} onNavigate={handleNavigate} />;
            default:
                return null;
        }
    };

    // Pages that hide the bottom nav
    const isSubPage = !mainTabs.includes(activeTab);

    if (!appReady) {
        return <div className="min-h-screen bg-black flex items-center justify-center"><LoadingScreen /></div>;
    }

    if (!isTelegram) {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-center">
                <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(59,130,246,0.5)]">
                    <svg viewBox="0 0 24 24" className="w-8 h-8 fill-white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-2h2v2h-2zm0-10v6h2V7h-2z" /></svg>
                </div>
                <h1 className="text-xl font-bold text-white mb-2">{tErrors('accessDenied')}</h1>
                <p className="text-white/50 text-sm max-w-xs">
                    {tErrors('accessDeniedMessage')}
                </p>
            </div>
        )
    }

    const handleGiftAccept = async () => {
        try {
            const definitionIds = newGifts.map(g => (g as any).definition_id);
            await apiFetch('/api/users/acknowledge-gift', { 
                method: 'POST',
                body: JSON.stringify({ definitionIds })
            });
        } catch (e) {
            console.error('Failed to acknowledge gift', e);
        }
        setNewGifts([]);
        // Re-fetch user to show the new cards in the inventory
        const tgid = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
        if (tgid) fetchUser(tgid, true);
    };

    return (
        <AppProvider>
            <NotificationProvider>
                <div className="min-h-screen bg-tg-bg text-tg-text font-sans relative selection:bg-content-accent/30 pt-safe pb-safe" id="main-scroll-container">

                {/* Premium Toast Notification */}
                <AnimatePresence mode="wait">
                    {currentNotification && (
                        <ToastNotification
                            key={currentNotification.id}
                            notification={currentNotification}
                            onClose={() => setCurrentNotification(null)}
                            onClick={() => {
                                const notif = currentNotification;
                                setCurrentNotification(null);
                                const type = notif.type?.toLowerCase();
                                const subType = notif.metadata?.type?.toLowerCase();
                                const metadata = notif.metadata || {};
                                if (type === 'p2p_buyer' || type === 'p2p_seller' || type === 'p2p_complete' || subType === 'p2p_buyer' || subType === 'p2p_seller' || subType === 'p2p_complete') {
                                    setManualP2PData(metadata);
                                    setActiveTab('p2p_transaction');
                                } else if (type === 'nft_withdrawal' || subType === 'nft_withdrawal') {
                                    setActiveReceiptData(metadata);
                                    setActiveTab('nft_receipt');
                                } else if (type === 'outbid' || type === 'auction_bid' || subType === 'outbid' || subType === 'auction_bid') {
                                    if (metadata.auctionId) localStorage.setItem('selected_auction_id', metadata.auctionId.toString());
                                    setActiveTab('auction_bid');
                                } else if (metadata.cardId) {
                                    setActiveCardId(metadata.cardId.toString());
                                    setActiveTab('card_view');
                                } else {
                                    setActiveTab('notifications');
                                }
                            }}
                        />
                    )}
                </AnimatePresence>

                {/* ═══════════════════════════════════════════════════════
                    KEEP-ALIVE MAIN TABS — Always mounted, show/hide via CSS
                    This is the core fix: no Unmount/Remount on tab switch
                ═══════════════════════════════════════════════════════ */}
                <Suspense fallback={<PyramidLoader />}>

                    {/* HOME — always mounted */}
                    <div
                        className={`pb-24 pt-[env(safe-area-inset-top)]`}
                        style={!(activeTab === 'home' && !isOnSubPage) ? { display: 'none' } : undefined}
                    >
                        <Home onNavigate={handleNavigate} onSearch={() => handleNavigate('search')} isActive={activeTab === 'home' && !isOnSubPage} />
                    </div>

                    {/* STORE — always mounted */}
                    <div
                        className="pb-24 pt-[env(safe-area-inset-top)]"
                        style={!(activeTab === 'store' && !isOnSubPage) ? { display: 'none' } : undefined}
                    >
                        <Store onNavigate={handleNavigate} onSearch={() => handleNavigate('search')} isActive={activeTab === 'store' && !isOnSubPage} />
                    </div>

                    {/* MY USER — always mounted */}
                    <div
                        className="pb-24 pt-[env(safe-area-inset-top)]"
                        style={!(activeTab === 'my_user' && !isOnSubPage) ? { display: 'none' } : undefined}
                    >
                        <MyUser onNavigate={handleNavigate} isActive={activeTab === 'my_user' && !isOnSubPage} />
                    </div>

                    {/* STAKING — always mounted */}
                    <div
                        className="pb-24 pt-[env(safe-area-inset-top)]"
                        style={!(activeTab === 'staking' && !isOnSubPage) ? { display: 'none' } : undefined}
                    >
                        <Staking onNavigate={handleNavigate} isActive={activeTab === 'staking' && !isOnSubPage} />
                    </div>

                    {/* OFFERS — always mounted */}
                    <div
                        className="pb-24 pt-[env(safe-area-inset-top)]"
                        style={!(activeTab === 'offers' && !isOnSubPage) ? { display: 'none' } : undefined}
                    >
                        <Offers onNavigate={handleNavigate} onSearch={() => handleNavigate('search')} isActive={activeTab === 'offers' && !isOnSubPage} />
                    </div>

                    {/* AUCTIONS — always mounted */}
                    <div
                        className="pb-24 pt-[env(safe-area-inset-top)]"
                        style={!(activeTab === 'auctions' && !isOnSubPage) ? { display: 'none' } : undefined}
                    >
                        <Auctions onNavigate={handleNavigate} onSearch={() => handleNavigate('search')} isActive={activeTab === 'auctions' && !isOnSubPage} />
                    </div>

                    {/* MY GIFTS & STAKING — removed */}

                    {/* PROFILE — always mounted */}
                    <div
                        className="pb-24 pt-[env(safe-area-inset-top)]"
                        style={!(activeTab === 'profile' && !isOnSubPage) ? { display: 'none' } : undefined}
                    >
                        <ProfilePush 
                            onBack={handleBack} 
                            onNavigate={handleNavigate} 
                            isActive={activeTab === 'profile' && !isOnSubPage}
                        />
                    </div>

                    {/* ADMIN — special page */}
                    {activeTab === 'admin' && (
                        <div className="pb-24 pt-[env(safe-area-inset-top)]">
                            <AdminDashboard onBack={handleBack} />
                        </div>
                    )}

                    {/* PREVIEW — special page */}
                    {activeTab === 'preview' && (
                        <CardPreviewPage cardId={activeCardId!} />
                    )}

                    {/* SUB-PAGES — rendered on top, mount/unmount normally */}
                    {isOnSubPage && (
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -8 }}
                                transition={{ duration: 0.18, ease: 'easeOut' }}
                                className="min-h-screen bg-tg-bg"
                            >
                                <div className={`${
                                    ['wallet', 'public_profile'].includes(activeTab) ? 'pt-0' : 'pt-[env(safe-area-inset-top)]'
                                } pb-24`}>
                                    {renderSubPage()}
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    )}

                </Suspense>

                {/* Bottom Navigation — shown only on main tabs */}
                <AnimatePresence>
                    {!isSubPage && (
                        <motion.div
                            initial={{ y: 100 }}
                            animate={{ y: 0 }}
                            exit={{ y: 100 }}
                            className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none"
                        >
                            <div id="tabnav" className="pointer-events-auto">
                                <TabNavigator
                                    activeTab={activeTab}
                                    onNavigate={handleNavigate}
                                    unreadCount={unreadNotifications}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Global Floating Scroll Buttons */}
                <ScrollButtons />

                {/* Navigation Loading Overlay — only for sub-pages */}
                <AnimatePresence>
                    {pageLoading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="fixed inset-0 z-[110] pointer-events-none"
                        >
                            <PyramidLoader />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Gift Reveal Modal for New Users */}
                {newGifts.length > 0 && (
                    <GiftRevealModal 
                        gifts={newGifts} 
                        onAccept={handleGiftAccept} 
                    />
                )}

                {/* Marketing channel gate — shows for users not subscribed to @KiteMarketplace */}
                <ChannelSubscribeModal enabled={appReady && isTelegram && !!user} />
                
                {/* Language selection for new users */}
                <LanguageSelectModal enabled={appReady && isTelegram && !!user} />
            </div>
        </NotificationProvider>
        </AppProvider>
    );
}

export default App;
